import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign, Lock, AlertCircle, CheckCircle, Clock,
  XCircle, ArrowRight, ShieldAlert, Loader
} from 'lucide-react';
import { formatCurrency, validatePin, calculateWithdrawalDays, parseAccountBalance } from '../utils/helpers';
import { useUser } from '../context/UserContext';
import apiService from '../services/api';
import { toast } from 'react-toastify';
import DashboardLayout from '../components/DashboardLayout';

const API = 'https://tradebot-k4nt.onrender.com/api';

/* ── fetch live Stripe account status ── */
const fetchStripeStatus = async (username) => {
  try {
    const res  = await fetch(`${API}/payment/retive-stripe-account-info/web/${username}`);
    const data = await res.json();
    return data?.allAccountObj ?? null;
  } catch {
    return null;
  }
};

/* ── parse a raw Stripe account value into a simple status ── */
const parseAcct = (rawValue) => {
  // value === 2 or integer = no account connected
  if (rawValue === 2 || rawValue === null || rawValue === undefined || (typeof rawValue === 'number' && rawValue >= 0)) {
    return { connected: false, approved: false, label: 'Not Connected', status: 'NOT_CONNECTED' };
  }
  const acct = rawValue;
  if (acct.charges_enabled && acct.payouts_enabled) {
    return { connected: true, approved: true, label: 'Approved & Active', status: 'APPROVED', email: acct.email, acct };
  }
  const req     = acct.requirements || {};
  const pastDue = req.past_due || [];
  if (pastDue.length > 0 || req.disabled_reason) {
    return { connected: true, approved: false, label: 'Restricted', status: 'RESTRICTED', email: acct.email, acct };
  }
  return { connected: true, approved: false, label: 'Pending Review', status: 'PENDING', email: acct.email, acct };
};

/* ── currency config for the withdrawal selector ── */
const CURRENCIES = [
  { key: 'USD', flag: '🇺🇸', symbol: '$', stripeKey: 'retrieveUSD', gradient: 'from-green-600 to-green-500', hoverGradient: 'hover:from-green-700 hover:to-green-600', label: 'Withdraw in USD' },
  { key: 'GBP', flag: '🇬🇧', symbol: '£', stripeKey: 'retrieveGBP', gradient: 'from-blue-600 to-blue-500',  hoverGradient: 'hover:from-blue-700 hover:to-blue-600',  label: 'Withdraw in GBP' },
  { key: 'YEN', flag: '🇯🇵', symbol: '¥', stripeKey: 'retrieveYEN', gradient: 'from-red-600 to-red-500',    hoverGradient: 'hover:from-red-700 hover:to-red-600',    label: 'Withdraw in YEN' },
];

const STATUS_ICON = {
  APPROVED:      { Icon: CheckCircle, cls: 'text-green-600', bg: 'bg-green-100' },
  PENDING:       { Icon: Clock,       cls: 'text-amber-600', bg: 'bg-amber-100' },
  RESTRICTED:    { Icon: XCircle,     cls: 'text-red-600',   bg: 'bg-red-100' },
  NOT_CONNECTED: { Icon: AlertCircle, cls: 'text-slate-500', bg: 'bg-slate-100' },
};

const Withdraw = () => {
  const navigate = useNavigate();
  const { user: contextUser } = useUser();

  const [user,             setUser]             = useState(null);
  const [loading,          setLoading]          = useState(false);
  const [stripeLoading,    setStripeLoading]    = useState(true);
  const [withdrawalDays,   setWithdrawalDays]   = useState(0);
  const [stripeAccounts,   setStripeAccounts]   = useState({ USD: null, GBP: null, YEN: null });
  const [amount,           setAmount]           = useState('');
  const [pin,              setPin]              = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [showPinModal,     setShowPinModal]     = useState(false);

  /* ── load stripe accounts ── */
  const loadStripeStatus = useCallback(async (username) => {
    setStripeLoading(true);
    const raw = await fetchStripeStatus(username);
    if (raw) {
      setStripeAccounts({
        USD: parseAcct(raw.retrieveUSD),
        GBP: parseAcct(raw.retrieveGBP),
        YEN: parseAcct(raw.retrieveYEN),
      });
    }
    setStripeLoading(false);
  }, []);

  useEffect(() => {
    if (!contextUser?.telegramName) { navigate('/login'); return; }
    setUser(contextUser);
    if (contextUser.dateCreated) setWithdrawalDays(calculateWithdrawalDays(contextUser.dateCreated));
    loadStripeStatus(contextUser.telegramName);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextUser?.telegramName, navigate, loadStripeStatus]);

  /* ── does the user have at least ONE approved account? ── */
  const anyApproved = Object.values(stripeAccounts).some(a => a?.approved);

  const handleCurrencySelection = (currencyKey) => {
    if (withdrawalDays > 0) {
      toast.error(`Please wait ${withdrawalDays} more days before withdrawing`);
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (parseFloat(amount) > parseAccountBalance(user?.accountBalance)) {
      toast.error('Insufficient balance');
      return;
    }
    const acct = stripeAccounts[currencyKey];
    if (!acct?.approved) {
      toast.error(`Your ${currencyKey} account is not verified yet. Please verify it first.`);
      return;
    }
    setSelectedCurrency(currencyKey);
    setShowPinModal(true);
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    if (!validatePin(pin)) { toast.error('Please enter a valid 6-digit PIN'); return; }
    setLoading(true);
    try {
      const response = await apiService.withdraw(
        user.telegramName,
        parseFloat(amount),
        '', '', '', pin,
        selectedCurrency
      );
      if (response.status === 200 || response.status === 201) {
        toast.success(`Withdrawal of ${formatCurrency(parseFloat(amount))} ${selectedCurrency} submitted!`);
        setShowPinModal(false);
        setAmount('');
        setPin('');
        setSelectedCurrency('');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Withdrawal failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-5">

        {/* Waiting period notice */}
        {withdrawalDays > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-2">
            <AlertCircle className="text-red-600 shrink-0" size={20} />
            <div>
              <p className="text-red-900 font-semibold">⏳ Withdrawals available in {withdrawalDays} day{withdrawalDays !== 1 ? 's' : ''}</p>
              <p className="text-sm text-red-700 mt-0.5">New accounts must wait 7 days before making withdrawals.</p>
            </div>
          </div>
        )}

        {/* No verified account banner */}
        {!stripeLoading && !anyApproved && (
          <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-5 flex items-start gap-4">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center shrink-0">
              <ShieldAlert size={24} className="text-amber-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-amber-900 text-base">No Verified Account</h3>
              <p className="text-sm text-amber-800 mt-1">
                You need to connect and verify at least one account (USD, GBP, or YEN) before you can withdraw funds.
              </p>
              <button
                onClick={() => navigate('/account-verification')}
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-xl transition-colors"
              >
                Verify an Account <ArrowRight size={15} />
              </button>
            </div>
          </div>
        )}

        {/* Main card */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
          {/* Balance */}
          <div className="text-center mb-8">
            <h3 className="text-lg text-slate-600 mb-2">
              <a href={`https://t.me/${user.telegramName.replace('@', '')}`}
                target="_blank" rel="noopener noreferrer"
                className="hover:text-blue-600 transition-colors">{user.telegramName}</a>
            </h3>
            <h2 className="text-4xl font-bold text-slate-900">{formatCurrency(parseAccountBalance(user?.accountBalance))}</h2>
          </div>

          {/* Amount input */}
          <div className="mb-8">
            <div className="relative">
              <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 z-10" size={24} />
              <input
                type="number" step="0.01" placeholder="Amount"
                value={amount} onChange={e => setAmount(e.target.value)}
                className="w-full pl-14 pr-4 py-4 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-2xl font-semibold text-center"
                disabled={withdrawalDays > 0}
              />
            </div>
          </div>

          {/* Connected account status + withdraw buttons */}
          <div className="space-y-3">
            <h5 className="text-sm font-medium text-slate-600 mb-1">Select Withdrawal Account:</h5>

            {stripeLoading ? (
              <div className="flex items-center justify-center py-8 gap-3 text-slate-500">
                <Loader size={20} className="animate-spin" />
                <span className="text-sm">Checking connected accounts…</span>
              </div>
            ) : (
              CURRENCIES.map(({ key, flag, symbol, stripeKey, gradient, hoverGradient, label }) => {
                const acct      = stripeAccounts[key];
                const approved  = acct?.approved;
                const statusCfg = STATUS_ICON[acct?.status || 'NOT_CONNECTED'];
                const StatusIcon = statusCfg.Icon;

                return (
                  <div key={key} className="rounded-2xl border border-slate-200 overflow-hidden">
                    {/* Status row */}
                    <div className={`flex items-center gap-3 px-4 py-2.5 ${statusCfg.bg}`}>
                      <StatusIcon size={15} className={statusCfg.cls} />
                      <span className="text-xs font-semibold text-slate-700">{flag} {key} Account</span>
                      <span className={`ml-auto text-xs font-bold ${statusCfg.cls}`}>{acct?.label || 'Not Connected'}</span>
                    </div>

                    {/* Withdraw button */}
                    <button
                      type="button"
                      onClick={() => handleCurrencySelection(key)}
                      disabled={withdrawalDays > 0 || !amount || !approved}
                      className={`w-full py-3.5 px-6 bg-gradient-to-r ${gradient} ${hoverGradient} text-white font-semibold transition-all shadow-sm
                        disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm`}
                    >
                      <span className="text-base">{symbol}</span>
                      <span>{label}</span>
                      {!approved && acct?.connected && (
                        <span className="ml-auto text-xs font-normal opacity-80">Pending verification</span>
                      )}
                      {!acct?.connected && (
                        <span
                          className="ml-auto text-xs font-normal underline opacity-90 cursor-pointer"
                          onClick={e => { e.stopPropagation(); navigate(`/connect-account?currency=${key}`); }}
                        >Connect account →</span>
                      )}
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Processing info */}
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
          <p className="text-sm text-blue-900">
            <strong>Processing Time:</strong> Withdrawals are typically processed within 1–3 business days.
            Only <strong>Approved &amp; Active</strong> accounts can receive funds.
          </p>
        </div>

        {/* PIN modal */}
        {showPinModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-slate-900">Confirm Withdrawal</h2>
                <button onClick={() => { setShowPinModal(false); setPin(''); }}
                  className="text-slate-400 hover:text-slate-600 transition-colors">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="mb-6 p-4 bg-slate-50 rounded-xl">
                <p className="text-sm text-slate-600 mb-1">Withdrawing to your {selectedCurrency} account</p>
                <p className="text-2xl font-bold text-slate-900">{formatCurrency(parseFloat(amount || 0))} {selectedCurrency}</p>
                {stripeAccounts[selectedCurrency]?.email && (
                  <p className="text-xs text-slate-500 mt-1">Stripe: {stripeAccounts[selectedCurrency].email}</p>
                )}
              </div>

              <form onSubmit={handleWithdraw} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Enter Your PIN</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                    <input
                      type="password" placeholder="6-digit PIN" value={pin}
                      onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength={6} required autoFocus
                      className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-center text-lg tracking-widest"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <button type="button" onClick={() => { setShowPinModal(false); setPin(''); }}
                    className="py-3 border-2 border-slate-300 text-slate-700 font-semibold rounded-lg hover:bg-slate-50 transition-all">
                    Cancel
                  </button>
                  <button type="submit" disabled={loading}
                    className="py-3 bg-slate-900 text-white font-semibold rounded-lg hover:bg-slate-800 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                    {loading ? (
                      <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Processing…</>
                    ) : 'Confirm'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Withdraw;
